import React, { useState, useRef, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { makeStyles } from "@material-ui/core/styles";
import { TextField, Typography } from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import SaveIcon from "@material-ui/icons/Save";

const useStyles = makeStyles((theme) => ({
  input: {
    paddingRight: 45,
    marginTop: 5,
    marginBottom: 5,
    color: theme.palette.primary.main,
  },
  inputLabel: {
    color: theme.palette.primary.main,
  },
  text: {
    paddingRight: 45,
    marginTop: 5,
    marginBottom: 5,
    color: "blue",
  },
}));

export default function FieldText(props) {
  const { fieldObject, register, errors } = props;
  const {
    fieldName,
    label,
    errorMessage,
    defaultValue,
    required,
    textSize,
    regex,
    min,
    max,
    step,
    sanitizeRegex,
  } = fieldObject;
  const classes = useStyles();
  const [editMode, setEditMode] = useState(true);
  // Mientras sea true, el helperText muestra el mismo errorMessage del
  // regex aunque react-hook-form todavía no haya validado nada (eso solo
  // pasa al enviar) — así el usuario entiende POR QUÉ se le borró el
  // carácter, no solo que desapareció.
  const [avisoFiltro, setAvisoFiltro] = useState(false);
  const ocultarAvisoRef = useRef(null);

  useEffect(() => {
    return () => clearTimeout(ocultarAvisoRef.current);
  }, []);

  // sanitizeRegex (opcional): caracteres a QUITAR del valor mientras se
  // escribe (ej. letras/símbolos en un teléfono, o cualquier cosa fuera del
  // alfabeto válido de un correo) — distinto de "regex", que solo valida al
  // enviar sin corregir nada. inputRef (no value/onChange) porque este
  // campo es no-controlado vía react-hook-form; mutar event.target.value
  // acá es lo que react-hook-form termina leyendo al validar/enviar.
  function limpiarValor(valorCrudo) {
    return valorCrudo.replace(new RegExp(sanitizeRegex, "g"), "");
  }

  // Si se borra en el mismo tick de la tecla, el carácter inválido nunca
  // llega a pintarse en pantalla — el usuario solo percibe que "no pasó
  // nada" al presionar la tecla, sin entender que el campo SÍ está
  // filtrando. Dejamos que se alcance a ver un instante antes de quitarlo,
  // y mostramos el aviso un poco más (2s, reiniciable) para que de tiempo
  // a leerlo.
  function handleChange(event) {
    if (!sanitizeRegex) return;
    const input = event.target;
    if (limpiarValor(input.value) === input.value) return;

    setTimeout(() => {
      const limpio = limpiarValor(input.value);
      if (limpio !== input.value) {
        input.value = limpio;
      }
    }, 150);

    setAvisoFiltro(true);
    clearTimeout(ocultarAvisoRef.current);
    ocultarAvisoRef.current = setTimeout(() => setAvisoFiltro(false), 2000);
  }

  // En blur no hay nada que "percibir" (el usuario ya se fue del campo) —
  // acá la limpieza es inmediata, de respaldo para autocompletado/
  // autocorrección que a veces no dispara un evento de cambio que React
  // capture.
  function handleBlur(event) {
    if (!sanitizeRegex) return;
    const limpio = limpiarValor(event.target.value);
    if (limpio !== event.target.value) {
      event.target.value = limpio;
    }
  }

  return (
    <TextField
      margin="none"
      className={classes.text}
      fullWidth
      label={label}
      disabled={!editMode}
      onChange={sanitizeRegex ? handleChange : undefined}
      onBlur={sanitizeRegex ? handleBlur : undefined}
      helperText={errors[fieldName] || avisoFiltro ? errorMessage : null}
      error={Boolean(errors[fieldName]) || avisoFiltro}
      defaultValue={defaultValue}
      inputRef={register({ required: required, pattern: new RegExp(regex) })}
      name={fieldName}
      type={max !== undefined || min !== undefined ? "number" : undefined}
      InputLabelProps={{
        className: classes.inputLabel,
      }}
      InputProps={{
        className: classes.input,
        inputProps: {
          max: max,
          min: min,
          step: step || 0,
        },
      }}
    />
  );
}
